import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { auth, db } from '../../firebase';
import { collection, addDoc, serverTimestamp, query, where, orderBy, limit, getDocs, doc, updateDoc, increment } from 'firebase/firestore';

const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  background: '#F8FDF8',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D',
  }
};

const EventPaymentDialog = ({ open, onClose, event, onPaymentSuccess }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const stripe = useStripe();
  const elements = useElements();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const [formData, setFormData] = useState({
    email: auth.currentUser?.email || '',
    phone: '',
    numberOfAttendees: 1,
    childrenNames: [''] // Array con los nombres de los niños
  });

  const steps = ['Información', 'Pago', 'Confirmación'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'numberOfAttendees') {
      const count = parseInt(value) || 1;
      const maxAllowed = Math.min(5, (event.totalPass || 0) - (event.sold || 0));

      // Validar que no exceda el máximo permitido
      if (count > maxAllowed) {
        setError(`Solo puedes comprar un máximo de ${maxAllowed} entradas`);
        return;
      }

      // Ajustar el array de nombres según el número de asistentes
      const newNames = Array(count).fill('').map((_, index) =>
        formData.childrenNames[index] || ''
      );
      setFormData(prev => ({
        ...prev,
        [name]: count,
        childrenNames: newNames
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    setError('');
  };

  const handleChildNameChange = (index, value) => {
    const newNames = [...formData.childrenNames];
    newNames[index] = value;
    setFormData(prev => ({
      ...prev,
      childrenNames: newNames
    }));
    setError('');
  };

  const handleNext = () => {
    // Validar paso 1
    if (activeStep === 0) {
      if (!formData.email || !formData.phone) {
        setError('Por favor, completa todos los campos obligatorios');
        return;
      }
      if (!formData.email.includes('@')) {
        setError('Por favor, introduce un email válido');
        return;
      }
      // Validar que todos los nombres de niños estén completos
      const allNamesComplete = formData.childrenNames.every(name => name.trim().length > 0);
      if (!allNamesComplete) {
        setError('Por favor, introduce el nombre de todos los niños');
        return;
      }
    }

    setActiveStep((prevStep) => prevStep + 1);
    setError('');
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError('');
  };

  const generateTicketNumber = async (eventId) => {
    try {
      // Obtener el prefijo del evento (primeras 4 letras en mayúsculas)
      const eventPrefix = event.titulo
        .replace(/[^a-zA-Z]/g, '')
        .substring(0, 4)
        .toUpperCase();

      // Consultar el último ticket del evento para obtener el número secuencial
      const ticketsQuery = query(
        collection(db, 'eventTickets'),
        where('eventId', '==', eventId),
        orderBy('purchaseDate', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(ticketsQuery);
      let nextNumber = 1;

      if (!querySnapshot.empty) {
        const lastTicket = querySnapshot.docs[0].data();
        // Extraer el número del último ticket (ej: "EVEN-0005" -> 5)
        const lastNumber = parseInt(lastTicket.ticketNumber.split('-')[1]) || 0;
        nextNumber = lastNumber + 1;
      }

      // Formatear el número con ceros a la izquierda (ej: 0001, 0002, etc.)
      const formattedNumber = String(nextNumber).padStart(4, '0');
      return `${eventPrefix}-${formattedNumber}`;
    } catch (error) {
      console.error('Error generating ticket number:', error);
      // Fallback: usar timestamp
      return `TICK-${Date.now()}`;
    }
  };

  const createTicketsInFirestore = async (paymentResult) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Usuario no autenticado');
      }

      const createdTickets = [];

      // Extraer nombre y apellido del usuario
      const displayName = user.displayName || '';
      const nameParts = displayName.split(' ');
      const userFirstName = nameParts[0] || '';
      const userLastName = nameParts.slice(1).join(' ') || '';

      // Crear un ticket individual para cada niño
      for (let i = 0; i < formData.childrenNames.length; i++) {
        const childName = formData.childrenNames[i];

        // Generar número de ticket único para cada niño
        const ticketNumber = await generateTicketNumber(event.id);

        // Crear el documento del ticket con el esquema correcto
        const ticketData = {
          ticketNumber: ticketNumber,
          eventId: event.id,
          eventName: event.titulo,
          eventDate: event.fechaInicio,
          location: event.ubicacion || '',
          childName: childName,
          price: event.precio,
          purchaseDate: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: 'purchased',
          ticketCount: 1,
          userId: user.uid,
          userEmail: formData.email,
          userFirstName: userFirstName,
          userLastName: userLastName,
          paymentMethod: paymentResult.data?.paymentMethod || 'card',
          notes: '',
          specialRequirements: ''
        };

        // Guardar en Firestore
        const docRef = await addDoc(collection(db, 'eventTickets'), ticketData);
        console.log(`Ticket creado para ${childName} con ID:`, docRef.id, 'Número:', ticketNumber);

        createdTickets.push({
          ticketId: docRef.id,
          ticketNumber: ticketNumber,
          childName: childName
        });
      }

      return {
        success: true,
        tickets: createdTickets,
        totalTickets: createdTickets.length
      };
    } catch (error) {
      console.error('Error creating tickets in Firestore:', error);
      throw error;
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Error al cargar Stripe. Por favor, recarga la página.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cardElement = elements.getElement(CardElement);

      // Crear token de Stripe
      const { error: stripeError, token } = await stripe.createToken(cardElement);

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Llamar al webhook de n8n para procesar el pago
      const webhookUrl = process.env.REACT_APP_N8N_PAYMENT_WEBHOOK_URL;
      if (!webhookUrl) {
        throw new Error('Webhook de pago no configurado');
      }

      const user = auth.currentUser;
      const paymentData = {
        action: "confirm_payment_intent",
        token: token.id,
        amount: event.precio * formData.numberOfAttendees * 100, // Stripe usa centavos
        currency: 'eur',
        description: `Evento AMPA - ${event.titulo}`,
        userId: user?.uid || 'guest',
        userEmail: formData.email,
        userName: user?.displayName || formData.childrenNames[0] || 'Usuario AMPA'
      };

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        throw new Error('Error al procesar el pago');
      }

      const result = await response.json();

      if (result.success) {
        // Crear los tickets en Firestore después del pago exitoso
        try {
          const ticketsResult = await createTicketsInFirestore(result);
          console.log(`${ticketsResult.totalTickets} tickets creados exitosamente`);

          // Incrementar el contador 'sold' del evento
          try {
            const eventRef = doc(db, 'events', event.id);
            await updateDoc(eventRef, {
              sold: increment(ticketsResult.totalTickets)
            });
            console.log(`Campo 'sold' incrementado en ${ticketsResult.totalTickets} para el evento ${event.id}`);
          } catch (updateError) {
            console.error('Error al actualizar el campo sold del evento:', updateError);
            // No bloqueamos el flujo si falla esta actualización
          }

          setPaymentSuccess(true);
          setActiveStep(2);

          // Llamar al callback de éxito después de un momento
          setTimeout(() => {
            if (onPaymentSuccess) {
              onPaymentSuccess({ ...result, tickets: ticketsResult });
            }
          }, 2000);
        } catch (ticketError) {
          console.error('Error al crear los tickets:', ticketError);
          // Aunque el pago fue exitoso, los tickets no se crearon
          setError('El pago se procesó correctamente, pero hubo un error al generar los tickets. Por favor, contacta con soporte.');
          setPaymentSuccess(true);
          setActiveStep(2);
        }
      } else {
        setError(result.message || 'Error al procesar el pago');
      }

    } catch (err) {
      console.error('Error processing payment:', err);
      setError(err.message || 'Error al procesar el pago. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const getTotalAmount = () => {
    return (event.precio * formData.numberOfAttendees).toFixed(2);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Información de contacto
            </Typography>

            <TextField
              fullWidth
              required
              type="email"
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              disabled={loading}
              helperText="Email del responsable de la compra"
            />

            <TextField
              fullWidth
              required
              label="Teléfono"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              disabled={loading}
              helperText="Teléfono de contacto"
            />

            <TextField
              fullWidth
              required
              type="number"
              label="Número de entradas"
              name="numberOfAttendees"
              value={formData.numberOfAttendees}
              onChange={handleInputChange}
              InputProps={{
                inputProps: {
                  min: 1,
                  max: Math.min(5, (event.totalPass || 0) - (event.sold || 0))
                }
              }}
              sx={{ mb: 3 }}
              disabled={loading}
              helperText={
                event.precio > 0
                  ? `Máximo 5 entradas por usuario. Disponibles: ${(event.totalPass || 0) - (event.sold || 0)}`
                  : "¿Cuántos niños asistirán? (Máximo 5)"
              }
            />

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
              Nombre de los asistentes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Las entradas son nominativas. Por favor, introduce el nombre completo de cada niño.
            </Typography>

            {formData.childrenNames.map((name, index) => (
              <TextField
                key={index}
                fullWidth
                required
                label={`Nombre del niño ${index + 1}`}
                value={name}
                onChange={(e) => handleChildNameChange(index, e.target.value)}
                sx={{ mb: 2 }}
                disabled={loading}
                placeholder="Ej: Juan García López"
              />
            ))}

            <Box sx={{
              mt: 3,
              p: 2,
              backgroundColor: COLORS.primary + '20',
              borderRadius: 2
            }}>
              <Typography variant="subtitle2" color="text.secondary">
                Resumen del pago
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLORS.success, mt: 1 }}>
                Total: {getTotalAmount()}€
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formData.numberOfAttendees} entrada(s) × {event.precio.toFixed(2)}€
              </Typography>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Información de pago
            </Typography>

            <Box sx={{
              p: 3,
              border: `1px solid ${COLORS.accent}40`,
              borderRadius: 2,
              backgroundColor: COLORS.background,
              mb: 3
            }}>
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: COLORS.text.primary,
                      '::placeholder': {
                        color: COLORS.text.secondary,
                      },
                    },
                    invalid: {
                      color: '#EF5350',
                    },
                  },
                  hidePostalCode: false,
                }}
              />
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                💳 Pago seguro con encriptación SSL
              </Typography>
              <Typography variant="caption">
                Tus datos de pago están protegidos y no se almacenan en nuestros servidores
              </Typography>
            </Alert>

            <Box sx={{
              mt: 2,
              p: 2,
              backgroundColor: COLORS.secondary + '20',
              borderRadius: 2
            }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Total a pagar: {getTotalAmount()}€
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Evento: {event.titulo}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Asistentes: {formData.numberOfAttendees}
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2, textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: COLORS.success, mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              ¡Pago completado!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {formData.numberOfAttendees === 1 ? 'La entrada ha sido confirmada' : 'Las entradas han sido confirmadas'}
            </Typography>

            <Box sx={{
              p: 3,
              backgroundColor: COLORS.primary + '20',
              borderRadius: 2,
              textAlign: 'left'
            }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Detalles de la inscripción:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Evento:</strong> {event.titulo}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Email:</strong> {formData.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Total pagado:</strong> {getTotalAmount()}€
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Asistentes ({formData.numberOfAttendees}):
              </Typography>
              {formData.childrenNames.map((name, index) => (
                <Typography key={index} variant="body2" color="text.secondary" sx={{ ml: 2, mb: 0.5 }}>
                  🎟️ {name}
                </Typography>
              ))}
            </Box>

            <Alert severity="success" sx={{ mt: 2 }}>
              Recibirás un correo de confirmación con los tickets de cada niño
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Si el pago fue exitoso, redirigir al portal
      if (paymentSuccess) {
        onClose();
        navigate('/portal');
      } else {
        onClose();
      }

      // Reset después de cerrar
      setTimeout(() => {
        setActiveStep(0);
        setError('');
        setPaymentSuccess(false);
        setFormData({
          email: auth.currentUser?.email || '',
          phone: '',
          numberOfAttendees: 1,
          childrenNames: ['']
        });
      }, 300);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon sx={{ color: COLORS.accent }} />
            <Typography variant={isMobile ? "subtitle1" : "h6"}>
              Inscripción al evento
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={loading} size={isMobile ? "small" : "medium"}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{isMobile ? '' : label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      {!paymentSuccess && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          {activeStep > 0 && activeStep < 2 && (
            <Button onClick={handleBack} disabled={loading}>
              Atrás
            </Button>
          )}
          {activeStep < 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              sx={{
                backgroundColor: COLORS.accent,
                '&:hover': { backgroundColor: COLORS.secondary }
              }}
            >
              Siguiente
            </Button>
          )}
          {activeStep === 1 && (
            <Button
              variant="contained"
              onClick={handlePayment}
              disabled={loading || !stripe}
              startIcon={loading && <CircularProgress size={20} />}
              sx={{
                backgroundColor: COLORS.success,
                '&:hover': { backgroundColor: COLORS.accent }
              }}
            >
              {loading ? 'Procesando...' : `Pagar ${getTotalAmount()}€`}
            </Button>
          )}
        </DialogActions>
      )}

      {paymentSuccess && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleClose}
            fullWidth
            sx={{
              backgroundColor: COLORS.success,
              '&:hover': { backgroundColor: COLORS.accent }
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default EventPaymentDialog;
